/**
 * Emergent Marketplace — Phase 1
 * VendorApplication.jsx
 * Architectural Layer: Presentation
 *
 * Multi-step vendor application form.
 * Design: Ritual/ceremony aesthetic — warm parchment, ember glow, serif gravitas.
 * Font: Playfair Display (display) + Lato (body)
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, Check, Store, FileText, CreditCard, Sparkles, MapPin, Info, RotateCcw } from "lucide-react";
import { apiFetch } from "./api";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Lato:wght@300;400;700&display=swap');

  .va-root {
    --parchment: #f8f2e4;
    --parchment-dark: #ede4ce;
    --ember: #c4622d;
    --ember-light: #e8845a;
    --ash: #4a4035;
    --ash-mid: #7a6e62;
    --ash-light: #b0a899;
    --smoke: #f0ebe0;
    --white: #ffffff;
    font-family: 'Lato', sans-serif;
    background: var(--parchment);
    min-height: 100vh;
    color: var(--ash);
  }

  /* Progress bar */
  .va-progress {
    background: var(--parchment-dark);
    padding: 0;
    position: sticky;
    top: 0;
    z-index: 40;
    box-shadow: 0 2px 12px rgba(74,64,53,0.08);
  }

  .va-progress-track {
    height: 3px;
    background: var(--parchment-dark);
    position: relative;
  }

  .va-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--ember), var(--ember-light));
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .va-progress-steps {
    display: flex;
    justify-content: space-between;
    padding: 16px 32px 12px;
  }

  .va-step-dot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .va-step-circle {
    width: 32px; height: 32px;
    border-radius: 50%;
    border: 2px solid var(--ash-light);
    background: var(--parchment);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ash-light);
    font-size: 13px;
    font-weight: 700;
    transition: all 0.3s;
  }

  .va-step-circle.active {
    border-color: var(--ember);
    background: var(--ember);
    color: var(--white);
    box-shadow: 0 0 0 4px rgba(196,98,45,0.15);
  }

  .va-step-circle.done {
    border-color: var(--ember);
    background: var(--ember);
    color: var(--white);
  }

  .va-step-label {
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ash-light);
    font-weight: 700;
  }

  .va-step-label.active { color: var(--ember); }

  /* Page container */
  .va-page {
    max-width: 560px;
    margin: 0 auto;
    padding: 40px 32px 80px;
    animation: va-fadeup 0.35s ease;
  }

  @keyframes va-fadeup {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .va-page-icon {
    width: 56px; height: 56px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(196,98,45,0.12), rgba(196,98,45,0.05));
    border: 1px solid rgba(196,98,45,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ember);
    margin-bottom: 20px;
  }

  .va-page-title {
    font-family: 'Playfair Display', serif;
    font-size: 30px;
    font-weight: 600;
    color: var(--ash);
    margin: 0 0 8px;
    line-height: 1.2;
  }

  .va-page-subtitle {
    font-size: 14px;
    color: var(--ash-mid);
    line-height: 1.65;
    margin: 0 0 36px;
  }

  /* Form fields */
  .va-field {
    margin-bottom: 24px;
  }

  .va-label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ash-mid);
    margin-bottom: 8px;
  }

  .va-input, .va-textarea, .va-select {
    width: 100%;
    background: var(--white);
    border: 1.5px solid var(--parchment-dark);
    border-radius: 10px;
    padding: 12px 16px;
    font-family: 'Lato', sans-serif;
    font-size: 15px;
    color: var(--ash);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .va-input:focus, .va-textarea:focus, .va-select:focus {
    border-color: var(--ember);
    box-shadow: 0 0 0 3px rgba(196,98,45,0.1);
  }

  .va-textarea {
    min-height: 100px;
    resize: vertical;
    line-height: 1.6;
  }

  .va-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a6e62' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 40px;
    cursor: pointer;
  }

  .va-hint {
    font-size: 12px;
    color: var(--ash-light);
    margin-top: 6px;
    line-height: 1.5;
  }

  .va-error {
    font-size: 12px;
    color: #c0392b;
    margin-top: 6px;
  }

  /* Slug preview */
  .va-slug-preview {
    display: flex;
    align-items: center;
    gap: 0;
    background: var(--smoke);
    border: 1.5px solid var(--parchment-dark);
    border-radius: 10px;
    overflow: hidden;
  }

  .va-slug-prefix {
    padding: 12px 14px;
    font-size: 13px;
    color: var(--ash-light);
    background: var(--parchment-dark);
    border-right: 1.5px solid var(--parchment-dark);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .va-slug-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    padding: 12px 16px;
    font-family: 'Lato', sans-serif;
    font-size: 15px;
    color: var(--ash);
  }

  /* Category checkboxes */
  .va-categories {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .va-cat-check {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1.5px solid var(--parchment-dark);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    background: var(--white);
  }

  .va-cat-check:hover { border-color: var(--ember-light); }

  .va-cat-check.selected {
    border-color: var(--ember);
    background: rgba(196,98,45,0.05);
  }

  .va-cat-check input { display: none; }

  .va-cat-box {
    width: 18px; height: 18px;
    border-radius: 5px;
    border: 2px solid var(--ash-light);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .va-cat-check.selected .va-cat-box {
    background: var(--ember);
    border-color: var(--ember);
  }

  .va-cat-label { font-size: 13px; color: var(--ash); font-weight: 400; }

  /* Payout model cards */
  .va-payout-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .va-payout-card {
    padding: 20px;
    border: 2px solid var(--parchment-dark);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s;
    background: var(--white);
    position: relative;
  }

  .va-payout-card:hover { border-color: var(--ember-light); }

  .va-payout-card.selected {
    border-color: var(--ember);
    background: rgba(196,98,45,0.03);
  }

  .va-payout-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .va-payout-card-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--ash);
    margin: 0 0 2px;
  }

  .va-payout-card-price {
    font-size: 20px;
    font-weight: 700;
    color: var(--ember);
  }

  .va-payout-card-desc {
    font-size: 13px;
    color: var(--ash-mid);
    line-height: 1.55;
  }

  .va-payout-radio {
    width: 20px; height: 20px;
    border-radius: 50%;
    border: 2px solid var(--ash-light);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .va-payout-card.selected .va-payout-radio {
    border-color: var(--ember);
  }

  .va-payout-radio-dot {
    width: 10px; height: 10px;
    border-radius: 50%;
    background: var(--ember);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .va-payout-card.selected .va-payout-radio-dot { opacity: 1; }

  /* Review section */
  .va-review-card {
    background: var(--white);
    border: 1px solid var(--parchment-dark);
    border-radius: 14px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .va-review-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--smoke);
    font-size: 14px;
  }

  .va-review-row:last-child { border-bottom: none; }

  .va-review-label { color: var(--ash-mid); }
  .va-review-value { color: var(--ash); font-weight: 700; }

  .va-terms {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 16px;
    background: var(--smoke);
    border-radius: 10px;
    margin-bottom: 24px;
    cursor: pointer;
  }

  .va-terms-check {
    width: 20px; height: 20px;
    border-radius: 5px;
    border: 2px solid var(--ash-light);
    flex-shrink: 0;
    margin-top: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .va-terms.checked .va-terms-check {
    background: var(--ember);
    border-color: var(--ember);
  }

  .va-terms-text {
    font-size: 13px;
    color: var(--ash-mid);
    line-height: 1.55;
  }

  .va-terms-text a { color: var(--ember); text-decoration: none; }

  /* Success screen */
  .va-success {
    text-align: center;
    padding: 60px 32px;
    animation: va-fadeup 0.5s ease;
  }

  .va-success-glow {
    width: 96px; height: 96px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(196,98,45,0.15), rgba(232,132,90,0.08));
    border: 1px solid rgba(196,98,45,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 28px;
    position: relative;
  }

  .va-success-glow::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 1px solid rgba(196,98,45,0.12);
  }

  .va-success-title {
    font-family: 'Playfair Display', serif;
    font-size: 32px;
    font-weight: 600;
    color: var(--ash);
    margin: 0 0 12px;
  }

  .va-success-body {
    font-size: 15px;
    color: var(--ash-mid);
    line-height: 1.7;
    max-width: 360px;
    margin: 0 auto 36px;
  }

  .va-success-steps {
    background: var(--white);
    border: 1px solid var(--parchment-dark);
    border-radius: 16px;
    overflow: hidden;
    text-align: left;
    max-width: 400px;
    margin: 0 auto;
  }

  .va-next-step {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--smoke);
    font-size: 13px;
    color: var(--ash-mid);
  }

  .va-next-step:last-child { border-bottom: none; }

  .va-next-num {
    width: 28px; height: 28px;
    border-radius: 50%;
    background: rgba(196,98,45,0.1);
    color: var(--ember);
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* Buttons */
  .va-btn-primary {
    width: 100%;
    padding: 14px;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--ember), #a84f22);
    border: none;
    color: var(--white);
    font-family: 'Lato', sans-serif;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .va-btn-primary:hover { opacity: 0.92; transform: translateY(-1px); }
  .va-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .va-btn-secondary {
    padding: 10px 20px;
    border-radius: 10px;
    background: transparent;
    border: 1.5px solid var(--parchment-dark);
    color: var(--ash-mid);
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: border-color 0.2s;
  }

  .va-btn-secondary:hover { border-color: var(--ash-mid); }

  .va-btn-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 32px;
    gap: 12px;
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: "store",    label: "Store",    icon: Store },
  { id: "details",  label: "Details",  icon: FileText },
  { id: "shipping", label: "Shipping", icon: MapPin },
  { id: "returns",  label: "Returns",  icon: RotateCcw },
  { id: "fees",     label: "Fees",     icon: CreditCard },
  { id: "review",   label: "Review",   icon: Sparkles },
];

const CATEGORIES = [
  "Spiritual Tools", "Crystals & Minerals", "Books & Writing",
  "Art", "Ritual Materials", "Artifacts",
  "Clothing", "Decorative", "e-Commerce",
];

const slugify = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// ─── Step Components ──────────────────────────────────────────────────────────

function StepStore({ data, onChange, errors }) {
  return (
    <>
      <div className="va-page-icon"><Store size={24} /></div>
      <h2 className="va-page-title">Name your sacred space</h2>
      <p className="va-page-subtitle">
        Your store name and URL are how seekers will find you in the 11/11 Collective Connection Marketplace.
      </p>

      <div className="va-field">
        <label className="va-label">Store Name</label>
        <input
          className="va-input"
          placeholder="e.g. Sacred Roots, The Still Point"
          value={data.store_name}
          onChange={e => {
            const name = e.target.value;
            onChange({ store_name: name, store_slug: slugify(name) });
          }}
        />
        {errors.store_name && <p className="va-error">{errors.store_name}</p>}
      </div>

      <div className="va-field">
        <label className="va-label">Store URL</label>
        <div className="va-slug-preview">
          <span className="va-slug-prefix">emergent.app/market/</span>
          <input
            className="va-slug-input"
            placeholder="your-store"
            value={data.store_slug}
            onChange={e => onChange({ store_slug: slugify(e.target.value) })}
          />
        </div>
        <p className="va-hint">Lowercase letters and hyphens only. Cannot be changed later.</p>
        {errors.store_slug && <p className="va-error">{errors.store_slug}</p>}
      </div>

      <div className="va-field">
        <label className="va-label">Categories <span style={{color:"#b0a899",fontWeight:400}}>(select all that apply)</span></label>
        <div className="va-categories">
          {CATEGORIES.map(cat => (
            <label
              key={cat}
              className={`va-cat-check ${data.categories.includes(cat) ? "selected" : ""}`}
            >
              <input
                type="checkbox"
                checked={data.categories.includes(cat)}
                onChange={e => {
                  const cats = e.target.checked
                    ? [...data.categories, cat]
                    : data.categories.filter(c => c !== cat);
                  onChange({ categories: cats });
                }}
              />
              <div className="va-cat-box">
                {data.categories.includes(cat) && <Check size={11} color="white" />}
              </div>
              <span className="va-cat-label">{cat}</span>
            </label>
          ))}
        </div>
        <p className="va-hint" data-testid="ecommerce-category-hint" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Info size={12} style={{ flexShrink: 0 }} />
          e-Commerce is for digital goods (electronic commerce).
        </p>
        {errors.categories && <p className="va-error">{errors.categories}</p>}
      </div>
    </>
  );
}

function StepDetails({ data, onChange, errors }) {
  return (
    <>
      <div className="va-page-icon"><FileText size={24} /></div>
      <h2 className="va-page-title">Tell your story</h2>
      <p className="va-page-subtitle">
        Share what you offer and the intention behind your work. This appears on your storefront.
      </p>

      <div className="va-field">
        <label className="va-label">Store Description</label>
        <textarea
          className="va-textarea"
          placeholder="Describe your offerings, your practice, and the transformation you support..."
          value={data.description}
          onChange={e => onChange({ description: e.target.value })}
          rows={5}
        />
        <p className="va-hint">{data.description.length}/500 characters. Be authentic — seekers resonate with genuine expression.</p>
        {errors.description && <p className="va-error">{errors.description}</p>}
      </div>

      <div className="va-field">
        <label className="va-label">What types of products will you offer?</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { value: "physical", label: "Physical Products", desc: "Crystals, tools, books, ritual items (shipped)" },
            { value: "digital",  label: "Digital Downloads", desc: "E-books, PDFs, audio, guided meditations" },
          ].map(({ value, label, desc }) => (
            <label
              key={value}
              className={`va-cat-check ${data.product_types.includes(value) ? "selected" : ""}`}
              style={{ flexDirection: "column", alignItems: "flex-start", gap: 2, padding: "14px 16px" }}
            >
              <input
                type="checkbox"
                checked={data.product_types.includes(value)}
                onChange={e => {
                  const types = e.target.checked
                    ? [...data.product_types, value]
                    : data.product_types.filter(t => t !== value);
                  onChange({ product_types: types });
                }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                <div className="va-cat-box" style={{ marginTop: 0 }}>
                  {data.product_types.includes(value) && <Check size={11} color="white" />}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ash)" }}>{label}</div>
                  <div style={{ fontSize: 12, color: "var(--ash-light)", marginTop: 1 }}>{desc}</div>
                </div>
              </div>
            </label>
          ))}
        </div>
        {errors.product_types && <p className="va-error">{errors.product_types}</p>}
      </div>
    </>
  );
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

function StepShipping({ data, onChange, errors }) {
  const addr = data.shipping_address || {};
  const isPhysical = (data.product_types || []).includes("physical");
  const updateAddr = (field, value) =>
    onChange({ shipping_address: { ...addr, [field]: value } });

  return (
    <>
      <div className="va-page-icon"><MapPin size={24} /></div>
      <h2 className="va-page-title">Ship-from address</h2>
      <p className="va-page-subtitle">
        {isPhysical
          ? "Where will your physical products ship from? This is used to calculate shipping rates for your customers."
          : "If you plan to ship physical products in the future, you can add your address now or update it later."
        }
      </p>

      <div className="va-field">
        <label className="va-label">Street Address {isPhysical && <span style={{color: "var(--ember)"}}>*</span>}</label>
        <input
          className="va-input"
          placeholder="123 Healing Way"
          value={addr.street || ""}
          onChange={e => updateAddr("street", e.target.value)}
          data-testid="ship-street"
        />
        {errors.ship_street && <p className="va-error">{errors.ship_street}</p>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="va-field">
          <label className="va-label">City {isPhysical && <span style={{color: "var(--ember)"}}>*</span>}</label>
          <input
            className="va-input"
            placeholder="Crystal Springs"
            value={addr.city || ""}
            onChange={e => updateAddr("city", e.target.value)}
            data-testid="ship-city"
          />
          {errors.ship_city && <p className="va-error">{errors.ship_city}</p>}
        </div>
        <div className="va-field">
          <label className="va-label">State {isPhysical && <span style={{color: "var(--ember)"}}>*</span>}</label>
          <select
            className="va-input"
            value={addr.state || ""}
            onChange={e => updateAddr("state", e.target.value)}
            data-testid="ship-state"
          >
            <option value="">Select state</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.ship_state && <p className="va-error">{errors.ship_state}</p>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="va-field">
          <label className="va-label">ZIP Code {isPhysical && <span style={{color: "var(--ember)"}}>*</span>}</label>
          <input
            className="va-input"
            placeholder="90210"
            value={addr.zip_code || ""}
            onChange={e => updateAddr("zip_code", e.target.value)}
            data-testid="ship-zip"
          />
          {errors.ship_zip && <p className="va-error">{errors.ship_zip}</p>}
        </div>
        <div className="va-field">
          <label className="va-label">Country</label>
          <input
            className="va-input"
            value="United States"
            disabled
            style={{ background: "var(--smoke)", color: "var(--ash-light)" }}
          />
        </div>
      </div>

      <p style={{ fontSize: 12, color: "var(--ash-light)", marginTop: 16, lineHeight: 1.6 }}>
        This address will be used to calculate shipping rates for customers. It is never shown publicly.
        You can update it anytime from your Practitioner Hub.
      </p>

    </>
  );
}


function StepReturns({ data, onChange, errors }) {
  const rp = data.return_policy;
  const set = (patch) => onChange({ return_policy: { ...rp, ...patch } });

  return (
    <>
      <div className="va-page-icon"><RotateCcw size={24} /></div>
      <h2 className="va-page-title">Your return policy</h2>
      <p className="va-page-subtitle">
        You set the rules for your own items — including saying no. Some goods genuinely can't come
        back (consumables, supplements, custom work). Whatever you choose is shown on your storefront
        and on every receipt, and it's what the platform enforces.
      </p>

      <div className="va-payout-cards">
        <div
          className={`va-payout-card ${rp.accepts_returns ? "selected" : ""}`}
          onClick={() => set({ accepts_returns: true })}
          data-testid="policy-accepts-returns"
        >
          <div className="va-payout-card-header">
            <div>
              <p className="va-payout-card-name">I accept returns</p>
              <p style={{ fontSize: 12, color: "var(--ash-light)", margin: 0 }}>Buyers get a window to ask</p>
            </div>
            <div className="va-payout-radio"><div className="va-payout-radio-dot" /></div>
          </div>
          <p className="va-payout-card-desc">
            Buyers can request a return within the window you choose. You still approve or decline each one.
          </p>
        </div>

        <div
          className={`va-payout-card ${!rp.accepts_returns ? "selected" : ""}`}
          onClick={() => set({ accepts_returns: false })}
          data-testid="policy-no-returns"
        >
          <div className="va-payout-card-header">
            <div>
              <p className="va-payout-card-name">No returns</p>
              <p style={{ fontSize: 12, color: "var(--ash-light)", margin: 0 }}>All sales final</p>
            </div>
            <div className="va-payout-radio"><div className="va-payout-radio-dot" /></div>
          </div>
          <p className="va-payout-card-desc">
            Right for consumables and anything that can't be resold once it leaves your hands.
            Buyers see this clearly before they buy.
          </p>
        </div>
      </div>

      {rp.accepts_returns ? (
        <>
          <div className="va-field">
            <label className="va-label">How long do buyers have? (days after delivery)</label>
            <select
              className="va-select"
              value={rp.window_days}
              onChange={e => set({ window_days: parseInt(e.target.value, 10) })}
              data-testid="policy-window-days"
            >
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
            {errors?.window_days && <p className="va-error">{errors.window_days}</p>}
          </div>

          <div className="va-field">
            <label className="va-label">How do you make it right?</label>
            <select
              className="va-select"
              value={rp.resolution}
              onChange={e => set({ resolution: e.target.value })}
              data-testid="policy-resolution"
            >
              <option value="refund">Refund</option>
              <option value="replace">Replacement</option>
              <option value="either">Either — buyer's choice</option>
            </select>
          </div>

          <div className="va-field">
            <label className="va-label">Who pays return shipping?</label>
            <select
              className="va-select"
              value={rp.return_shipping_paid_by}
              onChange={e => set({ return_shipping_paid_by: e.target.value })}
              data-testid="policy-return-shipping"
            >
              <option value="buyer">Buyer</option>
              <option value="vendor">Me, the vendor</option>
            </select>
          </div>

          <div className="va-field">
            <label className="va-label">Restocking fee (%) <span style={{ color: "#b0a899", fontWeight: 400 }}>optional</span></label>
            <input
              className="va-input"
              type="number"
              min="0"
              max="50"
              value={rp.restocking_fee_percent}
              onChange={e => set({ restocking_fee_percent: parseFloat(e.target.value) || 0 })}
              data-testid="policy-restocking-fee"
            />
            {errors?.restocking_fee_percent && <p className="va-error">{errors.restocking_fee_percent}</p>}
          </div>
        </>
      ) : (
        <div className="va-field">
          <label className="va-label">Why can't these items be returned?</label>
          <textarea
            className="va-textarea"
            placeholder="e.g. Supplements and consumables can't be returned to stock or resold once they leave our inventory."
            value={rp.non_returnable_note || ""}
            onChange={e => set({ non_returnable_note: e.target.value })}
            data-testid="policy-non-returnable-note"
          />
          {errors?.non_returnable_note && <p className="va-error">{errors.non_returnable_note}</p>}
        </div>
      )}

      <div className="va-field">
        <label className="va-label">Always excluded</label>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--ash)" }}>
          <input
            type="checkbox"
            checked={rp.excludes_digital}
            onChange={e => set({ excludes_digital: e.target.checked })}
            data-testid="policy-excludes-digital"
          />
          Digital downloads can't be returned once delivered
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--ash)", marginTop: 8 }}>
          <input
            type="checkbox"
            checked={rp.excludes_custom}
            onChange={e => set({ excludes_custom: e.target.checked })}
            data-testid="policy-excludes-custom"
          />
          Custom or made-to-order pieces can't be returned
        </label>
      </div>

      <p className="va-hint" style={{ marginTop: 12, display: "flex", alignItems: "flex-start", gap: 6 }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          Even with "no returns", a buyer can still bring a problem to our team as a dispute — you'll
          be told, and we only step in when something has clearly gone wrong.
        </span>
      </p>
    </>
  );
}

function StepFees() {
  return (
    <>
      <div className="va-page-icon"><CreditCard size={24} /></div>
      <h2 className="va-page-title">What selling costs</h2>
      <p className="va-page-subtitle">
        No listing fees, no monthly fee, and nothing to pay until you make a sale.
      </p>

      <div className="va-payout-cards" data-testid="vendor-fee-summary">
        <div className="va-payout-card selected">
          <div className="va-payout-card-header">
            <div>
              <p className="va-payout-card-name">Transaction fee</p>
              <p style={{ fontSize: 12, color: "var(--ash-light)", margin: 0 }}>Only when something sells</p>
            </div>
            <div className="va-payout-card-price">6.5%</div>
          </div>
          <p className="va-payout-card-desc">
            Charged on the item price plus what the buyer pays for shipping. Never charged on
            sales tax. Any coupon or deal you run comes off first, so you're only charged on what
            you actually collect.
          </p>
        </div>

        <div className="va-payout-card">
          <div className="va-payout-card-header">
            <div>
              <p className="va-payout-card-name">Card processing</p>
              <p style={{ fontSize: 12, color: "var(--ash-light)", margin: 0 }}>Paid by the buyer</p>
            </div>
            <div className="va-payout-card-price" style={{ fontSize: 22 }}>$0</div>
          </div>
          <p className="va-payout-card-desc">
            Your buyer covers card processing at checkout — 3% + $0.30 per order. It never comes
            out of your payout.
          </p>
        </div>
      </div>

      <div className="va-review-card" style={{ marginTop: 18 }} data-testid="vendor-fee-example">
        <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>A $40 item with $8 shipping</p>
        <div className="va-review-row">
          <span className="va-review-label">Buyer pays</span>
          <span className="va-review-value">$49.74</span>
        </div>
        <div className="va-review-row">
          <span className="va-review-label">Transaction fee (6.5% of $48)</span>
          <span className="va-review-value">−$3.12</span>
        </div>
        <div className="va-review-row">
          <span className="va-review-label">You receive</span>
          <span className="va-review-value">$44.88</span>
        </div>
      </div>

      <p style={{ fontSize: 12, color: "var(--ash-light)", marginTop: 20, lineHeight: 1.6 }}>
        Payouts are sent to your own bank account through Stripe Connect, which you'll set up after
        approval. Extra storefronts beyond your membership allowance are $5/month, and importing an
        existing catalog has a one-time fee based on its size.
      </p>
    </>
  );
}

function StepReview({ data, termsAccepted, setTermsAccepted }) {
  return (
    <>
      <div className="va-page-icon"><Sparkles size={24} /></div>
      <h2 className="va-page-title">Review & submit</h2>
      <p className="va-page-subtitle">
        Your application will be reviewed by the 11/11 Collective Connection team. You'll receive a response within 2–3 business days.
      </p>

      <div className="va-review-card">
        <div className="va-review-row">
          <span className="va-review-label">Store Name</span>
          <span className="va-review-value">{data.store_name}</span>
        </div>
        <div className="va-review-row">
          <span className="va-review-label">URL Slug</span>
          <span className="va-review-value">/{data.store_slug}</span>
        </div>
        <div className="va-review-row">
          <span className="va-review-label">Categories</span>
          <span className="va-review-value" style={{ textAlign: "right", maxWidth: "60%", fontSize: 13 }}>
            {data.categories.join(", ") || "None selected"}
          </span>
        </div>
        <div className="va-review-row">
          <span className="va-review-label">Product Types</span>
          <span className="va-review-value" style={{ textAlign: "right", maxWidth: "60%", fontSize: 13 }}>
            {data.product_types.join(", ") || "None selected"}
          </span>
        </div>
        <div className="va-review-row">
          <span className="va-review-label">Selling fee</span>
          <span className="va-review-value">6.5% per sale</span>
        </div>
        {data.shipping_address?.street && (
          <div className="va-review-row">
            <span className="va-review-label">Ship From</span>
            <span className="va-review-value" style={{ textAlign: "right", maxWidth: "60%", fontSize: 13 }}>
              {data.shipping_address.city}, {data.shipping_address.state} {data.shipping_address.zip_code}
            </span>
          </div>
        )}
      </div>

      <div
        className={`va-terms ${termsAccepted ? "checked" : ""}`}
        onClick={() => setTermsAccepted(v => !v)}
      >
        <div className="va-terms-check">
          {termsAccepted && <Check size={12} color="white" />}
        </div>
        <p className="va-terms-text">
          I agree to the <a href="/vendor-terms" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Vendor Terms of Service</a> and <a href="/marketplace-guidelines" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Marketplace Guidelines</a>. I understand that my application will be reviewed before my store goes live.
        </p>
      </div>
    </>
  );
}

function SuccessScreen({ storeName, onGoBack }) {
  return (
    <div className="va-success">
      <div className="va-success-glow">
        <Check size={36} color="#c4622d" strokeWidth={2.5} />
      </div>
      <h2 className="va-success-title">Application submitted</h2>
      <p className="va-success-body">
        <strong>{storeName}</strong> is in the queue. The 11/11 Collective Connection team will review your application and reach out within 2–3 business days.
      </p>

      <div className="va-success-steps">
        <div className="va-next-step">
          <div className="va-next-num">1</div>
          <span>Admin reviews your application and approves your store</span>
        </div>
        <div className="va-next-step">
          <div className="va-next-num">2</div>
          <span>Once approved, a Vendor Dashboard link will appear in your sidebar navigation</span>
        </div>
        <div className="va-next-step">
          <div className="va-next-num">3</div>
          <span>Complete Stripe Connect onboarding from your Vendor Dashboard — takes under 5 minutes</span>
        </div>
        <div className="va-next-step">
          <div className="va-next-num">4</div>
          <span>Your vendor dashboard unlocks — add products and go live</span>
        </div>
      </div>

      <button
        className="va-btn-primary"
        style={{ maxWidth: 320, margin: "32px auto 0" }}
        onClick={onGoBack}
      >
        Back to Marketplace
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorApplication({ onComplete, onBack }) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  // Carried over from the Practitioner Hub setup wizard so nothing is retyped
  const prefill = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    store_name: prefill.get("store_name") || "",
    store_slug: (prefill.get("store_name") || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
    description: prefill.get("store_description") || "",
    categories: [],
    product_types: [],
    return_policy: {
      accepts_returns: false,
      window_days: 30,
      resolution: "refund",
      return_shipping_paid_by: "buyer",
      restocking_fee_percent: 0,
      non_returnable_note: "",
      excludes_custom: true,
      excludes_digital: true,
    },
    shipping_address: {
      street: "",
      city: "",
      state: "",
      zip_code: "",
      country: "US",
    },
  });

  const update = (fields) => setForm(prev => ({ ...prev, ...fields }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.store_name.trim()) e.store_name = "Store name is required";
      if (!form.store_slug.trim()) e.store_slug = "Store URL is required";
      if (form.categories.length === 0) e.categories = "Select at least one category";
    }
    if (step === 1) {
      if (!form.description.trim()) e.description = "Description is required";
      if (form.description.length > 500) e.description = "Max 500 characters";
      if (form.product_types.length === 0) e.product_types = "Select at least one product type";
    }
    if (step === 2) {
      const addr = form.shipping_address;
      if (form.product_types.includes("physical")) {
        if (!addr.street.trim()) e.ship_street = "Street address is required for physical product sellers";
        if (!addr.city.trim()) e.ship_city = "City is required";
        if (!addr.state.trim()) e.ship_state = "State is required";
        if (!addr.zip_code.trim()) e.ship_zip = "ZIP code is required";
      }
    }
    if (step === 3) {
      const rp = form.return_policy;
      if (rp.accepts_returns && (!rp.window_days || rp.window_days < 1)) {
        e.window_days = "Give buyers at least 1 day to request a return";
      }
      if (!rp.accepts_returns && !(rp.non_returnable_note || "").trim()) {
        e.non_returnable_note = "Tell buyers why you can't accept returns (e.g. consumables)";
      }
      if (rp.restocking_fee_percent < 0 || rp.restocking_fee_percent > 50) {
        e.restocking_fee_percent = "Restocking fee must be between 0% and 50%";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate()) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    if (!termsAccepted) {
      setErrors({ terms: "Please accept the terms to continue" });
      return;
    }
    setLoading(true);
    try {
      await apiFetch("/vendor/apply", {
        method: "POST",
        body: JSON.stringify({
          store_name: form.store_name,
          store_slug: form.store_slug,
          description: form.description,
          categories: form.categories,
          shipping_address: form.shipping_address,
          return_policy: form.return_policy,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: "Submission failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const progress = ((step + (submitted ? 1 : 0)) / STEPS.length) * 100;

  if (submitted) {
    return (
      <>
        <style>{styles}</style>
        <div className="va-root">
          <SuccessScreen storeName={form.store_name} onGoBack={onBack} />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="va-root">

        {/* Progress */}
        <div className="va-progress">
          <div className="va-progress-steps">
            {STEPS.map((s, i) => (
              <div key={s.id} className="va-step-dot">
                <div className={`va-step-circle ${i === step ? "active" : i < step ? "done" : ""}`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <span className={`va-step-label ${i === step ? "active" : ""}`}>{s.label}</span>
              </div>
            ))}
          </div>
          <div className="va-progress-track">
            <div className="va-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="va-page">
          {step === 0 && <StepStore data={form} onChange={update} errors={errors} />}
          {step === 1 && <StepDetails data={form} onChange={update} errors={errors} />}
          {step === 2 && <StepShipping data={form} onChange={update} errors={errors} />}
          {step === 3 && <StepReturns data={form} onChange={update} errors={errors} />}
          {step === 4 && <StepFees />}
          {step === 5 && (
            <StepReview
              data={form}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
            />
          )}

          {errors.submit && (
            <p style={{ color: "#c0392b", fontSize: 13, marginTop: 8, textAlign: "center" }}>
              {errors.submit}
            </p>
          )}

          <div className="va-btn-row">
            {step > 0 ? (
              <button className="va-btn-secondary" onClick={back}>
                <ChevronLeft size={16} /> Back
              </button>
            ) : (
              <button className="va-btn-secondary" onClick={onBack}>
                <ChevronLeft size={16} /> Cancel
              </button>
            )}

            {step < STEPS.length - 1 ? (
              <button className="va-btn-primary" style={{ maxWidth: 200 }} onClick={next}>
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button
                className="va-btn-primary"
                style={{ maxWidth: 200 }}
                onClick={submit}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
